import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../../connector/api';

export default function GradeCategoryModal({ category, onClose, onSuccess }) {
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        weight: '10.00',
        color: '#3B82F6',
        is_active: true
    });

    useEffect(() => {
        if (category) {
            setFormData({
                name: category.name || '',
                weight: category.weight || '10.00',
                description: category.description || 'test, quiz',
                color: category.color || '#3B82F6',
                is_active: category.is_active ?? true
            });
        }
    }, [category]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim()) {
            toast.error("Category name is required.");
            return;
        }

        const weightNum = parseFloat(formData.weight);
        if (isNaN(weightNum) || weightNum < 0 || weightNum > 100) {
            toast.error("Weight must be between 0 and 100.");
            return;
        }

        setIsSaving(true);
        try {
            if (category?.id) {
                await api.put(`grade-categories/${category.id}/`, formData);
                toast.success("Grade category updated successfully!");
            } else {
                await api.post('grade-categories/', formData);
                toast.success("Grade category created successfully!");
            }
            onSuccess();
        } catch (err) {
            console.error("Failed to save grade category:", err);
            toast.error("Failed to save grade category.");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex font-dynamic-title items-center justify-center bg-zinc-900/40 backdrop-blur-xs p-4 overflow-y-auto">
            <div className="bg-white border border-zinc-200 rounded-lg shadow-xl w-full max-w-md overflow-hidden">
                
                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between bg-zinc-50/50">
                    <div>
                        <h3 className="text-sm font-bold text-zinc-900">
                            {category ? "Edit Grade Category" : "New Grade Category"}
                        </h3>
                        <p className="text-xs text-zinc-500 mt-0.5">
                            Define assignment group weight and display color.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-zinc-400 hover:text-zinc-600 text-lg font-semibold leading-none"
                    >
                        &times;
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-2">
                    
                    {/* Name */}
                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-zinc-700">Category Name</label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="e.g., Homework, Final Exam, Quiz"
                            className="w-full px-3 py-2 text-xs border border-zinc-300 rounded-lg focus:outline-none focus:border-blue-500"
                            required
                        />
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-semibold text-zinc-700">Description</label>
                        <textarea
                            name="description"
                            value={formData.description || ''}
                            onChange={handleChange}
                            placeholder="e.g., Short weekly assessments covering lecture material."
                            rows="2"
                            className="w-full px-3 py-2 text-xs border border-zinc-300 rounded-lg focus:outline-none focus:border-blue-500"
                        />
                    </div>

                    {/* Weight & Color */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-zinc-700">Weight (%)</label>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                name="weight"
                                value={formData.weight}
                                onChange={handleChange}
                                className="w-full px-3 py-2 text-xs border border-zinc-300 rounded-lg focus:outline-none focus:border-blue-500"
                                required
                            />
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-semibold text-zinc-700">Display Color</label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    name="color"
                                    value={formData.color}
                                    onChange={handleChange}
                                    className="w-9 h-9 p-0.5 border border-zinc-300 rounded-lg cursor-pointer bg-white"
                                />
                                <span className="font-mono text-xs text-zinc-500 uppercase">{formData.color}</span>
                            </div>
                        </div>
                    </div>

                    {/* Active Status */}
                    <div className="pt-2">
                        <label className="flex items-center gap-2 cursor-pointer text-xs font-medium text-zinc-700">
                            <input
                                type="checkbox"
                                name="is_active"
                                checked={formData.is_active}
                                onChange={handleChange}
                                className="rounded border-zinc-300 text-blue-600 focus:ring-blue-500"
                            />
                            Active Category
                        </label>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end gap-2 pt-4 border-t border-zinc-100">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-xs font-medium text-zinc-700 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="px-4 py-2 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                        >
                            {isSaving ? "Saving..." : "Save Category"}
                        </button>
                    </div>

                </form>
            </div>
        </div>
    );
}